<!--  -->
<template>
  <div id="background">
    <SessionDialog :external-page="false" />
    <!-- These are three modals Terms, PolicyAck, Paperless (Ecomm) -->
    <GoPaperlessAction
      @success="handleGoPaperlessActionSuccess"
      @error="handleGoPaperlessActionError"
      @snooze="handleGoPaperlessActionSnooze"
    />
    <!--VerifyPhone Modal -->
    <VerifyPhoneWrapper
      :allow-edit="true"
      :show="showVerifyPhoneWrapperModal"
      @show-successful-verify="onVerifyPhoneSuccess"
      @close-verify-phone-dialog="onCloseVerifyPhoneDialog"
      ref="verifyaccdash"
    />
    <NavigationConfirmation v-if="globalStore.showNavigationConfirmation" />
    <SuccessVerifyPhone
      v-if="showSuccessfulVerify"
      @close-success-verify-phone="closeSuccessVerifyPhone"
    />

    <HeaderComponent
      :nav-drawer="navDrawer"
      :is-hide-all-but-locale="isHideAllButLocale"
      @clickToCloseTheDrawer="navDrawer = false"
      @clickToOpenTheDrawer="navDrawer = true"
      @logout="logout"
    />
    <div v-if="showSignOutConfirmationMessage" class="modal">
      <MessageDialog
        :show-dialog="showSignOutConfirmationMessage"
        :has-button-two="true"
        type="warning"
        :title="$t('L_sign_out_confirmation')"
        :message="$t('L_confirmation_message')"
        :button-two-label="$t('L_cancel')"
        :button-one-label="$t('L_sign_out')"
        :is-persistent="true"
        @buttonTwoAction="closeTheSignOutConfirmationAlert"
        @buttonOneAction="closeTheSignOutConfirmationAndLogout"
      />
    </div>
    <NavigationDrawer
      v-if="!isMobileApp"
      v-model="navDrawer"
      @logout="logout"
      :is-hide-all-but-locale="isHideAllButLocale"
    />

    <LeftSidebar v-if="!isHideAllButLocale" />

    <v-main
      :style="style"
      id="main_content"
      tabindex="-1"
      :class="isAndroidOrIos ? 'androidTopSpacePlusMenuBar' : 'menuBarSpace'"
    >
      <Notifications
        @call-verify-phone-wrapper="showVerifyPhone"
        @view-notification="showNotificationDetail"
        v-if="isDashboard && globalStore.alertsAndNotifications.length > 0"
        context="noticeboard"
      />
      <NotificationDetail
        v-if="showNotificationDetailDialog"
        :show-notification-detail="showNotificationDetailDialog"
        :notification-id="notificationDetailId"
        @accept-action-clicked="hideNotificationDetail"
        @reject-action-clicked="hideNotificationDetail"
        @dismiss-action-clicked="hideNotificationDetail"
      />
      <AlertsComponent context="app" v-if="showAlertsBasedOnEnv" />
      <SpanishBanner v-if="globalStore.spanishBanner" />
      <v-container v-if="!isDashboard" class="pt-0 pb-0">
        <v-row v-if="goBackText != ''">
          <v-col cols="12" class="mt-4">
            <h4
              @click="goBack"
              class="return-home-link font-size--16 font-weight-regular"
              href="javascript:void(0)"
            >
              <v-icon :icon="mdiChevronLeft" />{{ goBackText }}
            </h4>
          </v-col>
        </v-row>
      </v-container>

      <router-view />

      <BottomNavigationForMobile
        v-if="isMobileApp"
        @logout="logout"
        :is-hide-all-but-locale="isHideAllButLocale"
      />
      <FooterComponent />
    </v-main>
    <Qualtrics :language="globalStore.locale" />
  </div>
</template>

<script lang="ts">
import { defineComponent, type ComponentOptions } from 'vue';
import MessageDialog from '../components/MessageDialog.vue';
import HeaderComponent from '@/components/Header/HeaderComponent.vue';
import NavigationDrawer from '@/components/NavigationDrawer/NavigationDrawer.vue';
import LeftSidebar from '@/components/LeftSidebar/LeftSidebar.vue';
import FooterComponent from '@/components/Footer/Footer.vue';
import GoPaperlessAction from '@/components/GoPaperlessAction/GoPaperlessAction.vue';
import AlertsComponent from '@/components/AlertsComponent.vue';
import Qualtrics from '@/components/Qualtrics.vue';
import { openInNewWindow } from '@/mixins/openNewWindow';
import SessionDialog from '@/components/SessionDialog/SessionDialog.vue';
import VerifyPhoneWrapper from '@/components/VerifyPhoneWrapper/VerifyPhoneWrapper.vue';
import Notifications from '../components/MyNotifications/Notifications.vue';
import NotificationDetail from '../components/MyNotifications/NotificationDetail.vue';
import SpanishBanner from '@/components/SpanishBanner.vue';
import SuccessVerifyPhone from '@/modules/home/myaccount/components/SuccessVerifyPhone/SuccessVerifyPhone.vue';
import NavigationConfirmation from '@/components/NavigationConfirmation/NavigationConfirmation.vue';
import { useCustomerStore } from '@/stores/customer/customer.store';
import { usePolicyStore } from '@/modules/policy/store/policy.store';
import { CapacitorFacade } from '@/plugins/capacitorPlugin';
import { useDebtSolutionsStore } from '@/modules/debtsolutions/store/debtsolutions.store';
import { useChildRiderStore } from '@/modules/childrider/store/childrider.store';
import { getNav } from '@/shared/navLogic';
import { mdiChartBox, mdiCreditCardOff, mdiShield, mdiDotsVertical, mdiChevronLeft } from '@mdi/js';
import BottomNavigationForMobile from '@/components/BottomNavigationForMobile/BottomNavigationForMobile.vue';
import { RouteNames } from '@/router/routeNames';
import { getAuthClient } from '@/plugins/auth';
import { mapActions, mapState, mapStores } from 'pinia';
import { useGlobalStore } from '@/stores/global/global.store';
import { LOCALES } from '@/shared/models/constants.models';
import { Environment } from '@/shared/models/appConfig.model';

const componentOptions: ComponentOptions = {
  name: 'DashboardLayout',
  components: {
    MessageDialog,
    HeaderComponent,
    NavigationDrawer,
    BottomNavigationForMobile,
    LeftSidebar,
    FooterComponent,
    GoPaperlessAction,
    SessionDialog,
    VerifyPhoneWrapper,
    AlertsComponent,
    Qualtrics,
    Notifications,
    NotificationDetail,
    SpanishBanner,
    SuccessVerifyPhone,
    NavigationConfirmation,
  },
  mixins: [openInNewWindow],
  data: () => ({
    languages: [] as LOCALES[],
    navDrawer: false,
    languagesPrefs: window.appConfig.languagePreferences,
    showSuccessfulVerify: false,
    showNotificationDetailDialog: false,
    notificationDetailId: -1,
    showVerifyPhoneWrapperModal: false,
    showSignOutConfirmationMessage: false,
    mdiShield,
    mdiChartBox,
    mdiCreditCardOff,
    mdiDotsVertical,
    nav: getNav(),
    mdiChevronLeft,
  }),
  computed: {
    ...mapStores(
      useGlobalStore,
      useCustomerStore,
      usePolicyStore,
      useDebtSolutionsStore,
      useChildRiderStore
    ),
    ...mapState(useGlobalStore, ['containsFeature', 'localeLanguages']),
    ...mapState(useGlobalStore, {
      locale: 'getLocale',
    }),
    ...mapState(usePolicyStore, {
      payMethodChange: 'payMethodChange',
      changeBankAccountStep: 'changeBankAccountStep',
      beneficiaryStep: 'beneficiaryStep',
      paymentStep: 'paymentStep',
      withDrawalDateStep: 'withDrawalDateStep',
    }),
    isAndroidOrIos() {
      return CapacitorFacade.isAndroid() || CapacitorFacade.isIos();
    },
    showAlertsBasedOnEnv() {
      return window.appConfig.env !== Environment.PROD; // This shows debugging logs, alerts, etc. in non-prod envs.
    },
    isMobileApp() {
      return CapacitorFacade.isIos() || CapacitorFacade.isAndroid();
    },
    isHideAllButLocale() {
      // This is where we need to pass the flag to hide all but locale selector
      if (this.isMobileApp) {
        return false;
      } else {
        return (
          this.beneficiaryStep > 0 ||
          this.payMethodChange > 0 ||
          this.changeBankAccountStep > 0 ||
          this.paymentStep > 0 ||
          this.withDrawalDateStep > 0
        );
      }
    },
    helpFlag() {
      const globalStore = useGlobalStore();
      return globalStore.containsFeature('HELP');
    },
    goBackText() {
      if (!this.isDashboard) {
        if (this.isDocumentListRoute) {
          return this.$t('L_back');
        }
        return ''; // this.$t('L_return_to_financial_home');
      }
      return '';
    },
    isDashboard() {
      return this?.$route?.name === 'Dashboard';
    },
    isDocumentListRoute() {
      return this?.$route?.name === 'DocumentListMobile';
    },
    style() {
      const style: Record<string, string | number> = {
        paddingTop: 0,
        marginLeft: '0px',
      };
      if (this.$vuetify?.display?.lgAndUp && !this.isHideAllButLocale) {
        style.marginLeft = '238px';
      }
      return style;
    },
    isMobile() {
      return this.$vuetify.display.xs;
    },
  },
  methods: {
    ...mapActions(useChildRiderStore, ['crcInit']),
    onVerifyPhoneSuccess() {
      this.showVerifyPhoneWrapperModal = false;
      this.globalStore.getAlerts();
      this.showSuccessfulVerify = true;
    },
    onCloseVerifyPhoneDialog() {
      this.showVerifyPhoneWrapperModal = false;
      this.globalStore.getAlerts();
      this.showSuccessfulVerify = false;
    },
    closeSuccessVerifyPhone() {
      this.showSuccessfulVerify = false;
    },
    showNotificationDetail(alertId: number) {
      this.showNotificationDetailDialog = true;
      this.notificationDetailId = alertId;
    },
    async hideNotificationDetail() {
      this.showNotificationDetailDialog = false;
      this.notificationDetailId = -1;
      await this.globalStore.getAlerts();
    },
    showVerifyPhone() {
      this.globalStore.verifyPhonePageToLand = 'first';
      this.showVerifyPhoneWrapperModal = true;
    },
    handleGoPaperlessActionSuccess() {
      this.globalStore.getAlerts();
    },
    handleGoPaperlessActionError() {
      this.globalStore.getAlerts();
    },
    handleGoPaperlessActionSnooze() {
      this.globalStore.getAlerts();
    },
    async helpAction() {
      if (this.helpFlag) {
        const newRightAnswersUrl =
          this.locale === 'fr_CA'
            ? window.appConfig.endpoints.frRightanswerslinkpostjune2025
            : window.appConfig.endpoints.rightanswerslinkpostjune2025;
        const isWeb = CapacitorFacade.isWeb();
        await this.openInNewWindow({
          url: newRightAnswersUrl,
          inappbrowser: !isWeb,
          target: 'RADashboardWinName',
        });
      }
    },
    goBack() {
      if (!this.isDashboard) {
        if (this.isDocumentListRoute && this.isMobile) {
          this.$router.push({ name: RouteNames.DOCUMENTS });
        } else {
          this.nav.goToMyFinancialHome();
        }
      }
    },
    closeTheSignOutConfirmationAlert() {
      this.showSignOutConfirmationMessage = false;
    },
    async closeTheSignOutConfirmationAndLogout() {
      if (CapacitorFacade.isNativePlatform()) {
        // ! clear browser session and revoke tokens
        await getAuthClient()
          .logout()
          .catch(async (e: { message: string; code: string } | undefined) => {
            try {
              this.$dynatrace.reportCustomError(
                'PC2_AUTH_ERR',
                `[AuthError] ${e?.code}: ${e?.message ?? JSON.stringify(e)}`
              );
              await this.globalStore.postAuditLog({
                actionId: 'Auth_Client_Logout_Native_FailureAction',
                details: `[${Date.now()}] ERROR CODE ${e?.code}`,
                message: e?.message,
              });
            } catch (_error) {
              /* empty */
            }
          });
        this.showSignOutConfirmationMessage = false;
        await this.globalStore.resetStore();
        await this.$router.push({ name: RouteNames.POST_LOGOUT });
      } else {
        // ! clear browser session and revoke tokens
        await getAuthClient()
          .logout()
          .catch(async (e: { message: string; code: string } | undefined) => {
            try {
              this.$dynatrace.reportCustomError(
                'PC2_AUTH_ERR',
                `[AuthError] ${e?.code}: ${e?.message ?? JSON.stringify(e)}`
              );
              await this.globalStore.postAuditLog({
                actionId: 'Auth_Client_Logout_Non_Native_FailureAction',
                details: `[${Date.now()}] ERROR CODE ${e?.code}`,
                message: e?.message,
              });
            } catch (_error) {
              /* empty */
            }
          });
        this.showSignOutConfirmationMessage = false;
        await this.globalStore.resetStore();
      }
    },
    logout() {
      if (CapacitorFacade.isNativePlatform()) {
        this.showSignOutConfirmationMessage = true;
      } else {
        // ! clear browser session and revoke tokens
        getAuthClient()
          .logout()
          .catch(async (e: { message: string; code: string } | undefined) => {
            try {
              this.$dynatrace.reportCustomError(
                'PC2_AUTH_LOGOUT_DASHBOARD_ERR',
                `[AuthLogOutDashboardError] ${e?.code}: ${e?.message ?? JSON.stringify(e)}`
              );
              await this.globalStore.postAuditLog({
                actionId: 'Auth_Log_Out_Dashboard_FailureAction',
                details: `[${Date.now()}] ERROR CODE ${e?.code}`,
                message: e?.message,
              });
            } catch (_error) {
              /* empty */
            }
          });
      }
    },
  },
  async mounted() {
    try {
      const appDiv = document.getElementById('main_content');
      if (this.isAndroidOrIos && appDiv) {
        appDiv.style.paddingTop = '35px';
        appDiv.style.background = '#0b3862';
      }
      if (this.$route.name === 'Dashboard') {
        await this.policyStore.getPolicyOverview();
      }
      this.globalStore.getAlerts();
      this.globalStore.rootLoading = false;
      this.globalStore.validateLanguagePreference();
      if (this.globalStore.locale?.indexOf('es') > -1) {
        this.globalStore.spanishBanner = true;
      }
      window.setTimeout(async () => {
        await this.globalStore.getDocuments();
      }, 0);
      await this.crcInit();
    } catch (_e) {
      /* empty */
    }
  },
};

export default defineComponent(componentOptions);
</script>

<style lang="scss" scoped>
.androidTopSpacePlusMenuBar {
  padding-top: 85px !important;
  margin-bottom: 120px;
}

.menuBarSpace {
  padding-top: 64px !important;
  margin-bottom: 180px;
}

#background {
  min-height: 100%;
  background-image: linear-gradient(to top, #607c97, #063562);
}

.return-home-link {
  color: #ffffff !important;

  .v-icon {
    color: #fff;
    font-size: 2em;
    line-height: 0.6em;
    margin-left: -8px;
  }
}

#alerts {
  position: -webkit-sticky;
  position: sticky;
  top: 4rem;
  z-index: 1;
}
</style>
